import { useEffect, useMemo, useState } from "react";
import { blogArticles as staticArticles } from "@/data/blogArticles";
import type { BlogArticle } from "@/data/blogArticles";
import { signArticleImageUrls, signArticleImageUrl } from "@/lib/storage";

export type { BlogArticle };
import { supabase } from "@/integrations/supabase/client";

const legacyArticleSlugs: Record<string, string> = {
  "marketing-for-ai-agents-meta-moltbook-acquisition": "marketing-for-superintelligence-agents-meta-moltbook-acquisition",
  "why-your-brand-is-invisible-to-ai-buyers": "why-your-brand-is-invisible-to-superintelligence-buyers",
  "ai-driven-performance-marketing-2026": "superintelligence-driven-performance-marketing-2026",
  "ai-process-automation": "superintelligence-process-automation",
  "agentic-ai-meetings": "agentic-superintelligence-meetings",
  "ai-overviews-and-branded-search-a-cmos-guide-to-erosion-control": "superintelligence-overviews-and-branded-search-a-cmos-guide-to-erosion-control",
  "ai-overviews-are-reshaping-brand-search-a-playbook-for-cmos": "superintelligence-overviews-are-reshaping-brand-search-a-playbook-for-cmos",
  "ais-impact-on-the-agency-model-what-cmos-need-to-know-about-the-shifting-landsca": "superintelligences-impact-on-the-agency-model-what-cmos-need-to-know-about-the-shifting-landsca",
  "on-device-ai-and-the-end-of-third-party-ad-attribution": "on-device-superintelligence-and-the-end-of-third-party-ad-attribution",
  "predictive-ai-what-real-time-campaign-optimisation-means-for-cmos": "predictive-superintelligence-what-real-time-campaign-optimisation-means-for-cmos",
  "reddits-ai-training-deal-a-new-frontier-for-brand-seo": "reddits-superintelligence-training-deal-a-new-frontier-for-brand-seo",
};

interface GeneratedArticleRow {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  date: string;
  date_modified: string | null;
  read_time: string;
  image_url: string;
  category: string;
  seo_title: string | null;
  seo_description: string | null;
  faqs: { question: string; answer: string }[] | null;
  cta: {
    title: string;
    text: string;
    buttonText: string;
    buttonUrl: string;
  } | null;
}

function mapRowToArticle(row: GeneratedArticleRow): BlogArticle {
  return {
    slug: row.slug,
    title: row.title,
    excerpt: row.excerpt,
    content: row.content,
    date: row.date,
    dateModified: row.date_modified || undefined,
    readTime: row.read_time,
    image: row.image_url,
    category: row.category,
    seoTitle: row.seo_title || undefined,
    seoDescription: row.seo_description || undefined,
    faqs: row.faqs || undefined,
    cta: row.cta
      ? {
          headline: row.cta.title,
          subtext: row.cta.text,
          buttonLabel: row.cta.buttonText,
          buttonHref: row.cta.buttonUrl,
        }
      : undefined,
  };
}

function sortNewestFirst(a: BlogArticle, b: BlogArticle): number {
  const da = Date.parse(a.date) || 0;
  const db = Date.parse(b.date) || 0;
  return db - da;
}

export function useArticles(limit?: number) {
  const [generated, setGenerated] = useState<BlogArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchGenerated() {
      try {
        const { data, error: dbError } = await supabase
          .from("generated_articles")
          .select("*")
          .eq("status", "published")
          .order("published_at", { ascending: false });

        if (cancelled) return;
        if (dbError) throw dbError;

        const mapped = (data || []).map((row) =>
          mapRowToArticle(row as unknown as GeneratedArticleRow)
        );
        const signed = await signArticleImageUrls(mapped);
        setGenerated(signed);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load articles");
      } finally {
        setLoading(false);
      }
    }

    fetchGenerated();
    return () => {
      cancelled = true;
    };
  }, []);

  const articles = useMemo(() => {
    const merged = [...staticArticles, ...generated].sort(sortNewestFirst);
    return limit ? merged.slice(0, limit) : merged;
  }, [generated, limit]);

  return { articles, loading, error };
}

export function useArticle(slug: string | undefined) {
  const resolvedSlug = slug ? legacyArticleSlugs[slug] || slug : undefined;
  const [article, setArticle] = useState<BlogArticle | undefined>(() =>
    resolvedSlug ? staticArticles.find((a) => a.slug === resolvedSlug) : undefined
  );
  const [loading, setLoading] = useState(!article);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!resolvedSlug) {
      setLoading(false);
      return;
    }

    const staticMatch = staticArticles.find((a) => a.slug === resolvedSlug);
    if (staticMatch) {
      setArticle(staticMatch);
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchGenerated() {
      try {
        const { data, error: dbError } = await supabase
          .from("generated_articles")
          .select("*")
          .eq("slug", resolvedSlug)
          .eq("status", "published")
          .single();

        if (cancelled) return;
        if (dbError) throw dbError;

        if (data) {
          const mapped = mapRowToArticle(data as unknown as GeneratedArticleRow);
          mapped.image = await signArticleImageUrl(mapped.image);
          setArticle(mapped);
        } else {
          setArticle(undefined);
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load article");
      } finally {
        setLoading(false);
      }
    }

    fetchGenerated();
    return () => {
      cancelled = true;
    };
  }, [resolvedSlug]);

  return { article, loading, error };
}
